import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { C } from "./ipd-billing/constants";
import SvgIcon from "./ipd-billing/SvgIcon";
import IPDBillingLayout from "./ipd-billing/components/IPDBillingLayout";
import IpdDateField from "./ipd/utils/IpdDateField";
import { resolveAdmissionId, searchChargeCaptureServices, bulkPostCharges, listAllocatedAdmissions } from "./ipd-billing/billing-api";
import { previewCorporatePricing } from "./ipd-billing/corporate-api";

const CHARGE_CAPTURE_PATH = "/ipd-billing/charge-capture";

// ── SEARCHABLE MULTI-SELECT (Medical Service) ────────────────────────────────
// Live search against GET .../ipd/billing/charge-capture/services/?name=&limit=
// (the Add Charge search — not /search_medical_services/). Each option carries
// its readonly `rate`/`fee` so the form can bind Rate (₹) directly.
function MedicalServiceSelect({ selectedIds, onToggle, placeholder, admissionId }) {
  const [open, setOpen]       = useState(false);
  const [search, setSearch]   = useState("");
  const [options, setOptions] = useState([]);

  // Debounced live search against the charge-capture service catalogue.
  useEffect(() => {
    const name = search.trim();
    if (name.length < 2) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const services = await searchChargeCaptureServices(name, 50, admissionId);
        if (cancelled) return;
        setOptions(services);
      } catch (err) {
        if (!cancelled) {
          console.error("Medical service search error:", err);
          setOptions([]);
        }
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search, admissionId]);

  const inputStyle = { width: "100%", padding: "9px 12px", border: `1px solid ${C.border}`, borderRadius: "7px", fontSize: "13px", color: C.text, outline: "none", boxSizing: "border-box", background: "#fff", boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.06)" };
  const count = selectedIds.length;
  const label = count === 0
    ? (placeholder || "Select medical service")
    : `${count} medical service${count > 1 ? "s" : ""} selected`;

  return (
    <div style={{ position: "relative" }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ ...inputStyle, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", textAlign: "left" }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: count ? C.text : C.textSub }}>
          {label}
        </span>
        <SvgIcon name="chevron-down" size={16} color={C.textSub} />
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 5 }} />
          <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 6, background: "#fff", border: `1px solid ${C.border}`, borderRadius: "7px", boxShadow: "0 8px 24px rgba(13,35,63,0.18)", overflow: "hidden" }}>
            <div style={{ padding: "8px", borderBottom: `1px solid ${C.border}` }}>
              <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search medical service…" style={{ ...inputStyle, padding: "7px 10px" }} />
            </div>
            <div style={{ maxHeight: "260px", overflowY: "auto" }}>
              {search.trim().length < 2 ? (
                <div style={{ padding: "12px", fontSize: "12px", color: C.textSub }}>Type at least 2 characters to search.</div>
              ) : options.length === 0 ? (
                <div style={{ padding: "12px", fontSize: "12px", color: C.textSub }}>No medical services found.</div>
              ) : (
                options.map((o, idx) => {
                  const checked = selectedIds.includes(o.id);
                  return (
                    <div key={`${o.id}-${idx}`}
                      onClick={() => onToggle(o)}
                      style={{ padding: "8px 12px", borderTop: idx === 0 ? "none" : `1px solid ${C.border}`, cursor: "pointer", fontSize: "13px", fontWeight: "600", color: C.text, display: "flex", alignItems: "center", gap: "9px" }}
                      onMouseEnter={e => { e.currentTarget.style.background = "#f8f9fa"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                    >
                      <span style={{ width: "16px", height: "16px", flexShrink: 0, borderRadius: "4px", border: `1.5px solid ${checked ? C.primary : C.border}`, background: checked ? C.primary : "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {checked && <SvgIcon name="check" size={11} color="#fff" />}
                      </span>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.name}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── ADD CHARGE PAGE ───────────────────────────────────────────────────────────
// Full-page version of the former Add Charge modal, reached from the "Add Charge"
// button on Charge Capture. Route state carries { admissionId, defaultChargeDate,
// patientName }; admissionId falls back to sessionStorage so a refresh still works.
export default function IPDBillingAddCharge() {
  const location = useLocation();
  const history  = useHistory();

  const admissionId = location.state?.admissionId || resolveAdmissionId(null) || "";
  const patientName = location.state?.patientName || "";
  const today = location.state?.defaultChargeDate || new Date().toISOString().split("T")[0];

  // Unsaved-draft persistence — keyed per admission so a half-filled Add Charge
  // survives navigating back to Charge Capture and returning. Cleared only on a
  // successful post (bulkPostCharges) or once the last line item is removed.
  const draftKey = admissionId ? `ipd_add_charge_draft_${admissionId}` : null;
  const loadDraft = () => {
    if (!draftKey) return {};
    try { return JSON.parse(sessionStorage.getItem(draftKey)) || {}; } catch { return {}; }
  };

  const [date, setDate]           = useState(() => loadDraft().date || today);
  // One line item per selected medical service; each carries its own qty & rate.
  const [lineItems, setLineItems] = useState(() => loadDraft().lineItems || []);
  const [posting, setPosting]     = useState(false);
  const [error, setError]         = useState("");
  // Corporate contract pricing (auto — resolved from the admission's payer).
  //   previews: serviceId → { applied:true, corp, std, disc } | { applied:false }
  //   skipCorporate: bill at rack rate instead (POST skip_corporate_pricing:true).
  const [previews, setPreviews]           = useState({});
  // The date the patient was ADMITTED, which is the date corporate pricing resolves on -
  // never the charge date. Held here so the preview asks the same question capture will.
  // null = not known yet; "" = looked for and not found.
  const [admittedOn, setAdmittedOn]       = useState(() => loadDraft().admittedOn ?? null);
  const [skipCorporate, setSkipCorporate] = useState(() => loadDraft().skipCorporate || false);
  const depts = ["ICU", "Nursing", "Lab", "Radiology", "Pharmacy", "Procedures", "Consultation", "Consumables", "OT"];

  const goBack = () => history.push(CHARGE_CAPTURE_PATH);

  // Persist the working draft on every edit; drop it once there's nothing to keep.
  useEffect(() => {
    if (!draftKey) return;
    if (lineItems.length === 0) {
      sessionStorage.removeItem(draftKey);
      return;
    }
    try {
      sessionStorage.setItem(draftKey, JSON.stringify({ date, lineItems, skipCorporate, admittedOn }));
    } catch { /* quota/serialization — draft is best-effort */ }
  }, [draftKey, date, lineItems, skipCorporate, admittedOn]);

  // Corporate pricing is locked to the tie-up that was in force on the ADMISSION date - a
  // patient keeps the rate card they were admitted under for the whole stay, and is not moved
  // onto one that starts mid-stay. Capture enforces that; the preview has to ask with the same
  // date or it quotes a price the save will not honour. The screen is only handed an
  // admissionId, so the date is looked up once here.
  useEffect(() => {
    if (!admissionId || admittedOn !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await listAllocatedAdmissions("");
        const match = (rows || []).find(
          (r) => String(r.admission_id ?? r.id) === String(admissionId)
        );
        const raw = match?.admitted_at;
        if (!cancelled) setAdmittedOn(raw ? String(raw).slice(0, 10) : "");
      } catch {
        // Leave it unknown rather than guessing a date - the preview stays hidden below.
        if (!cancelled) setAdmittedOn("");
      }
    })();
    return () => { cancelled = true; };
  }, [admissionId, admittedOn]);

  // No admission in context — bounce back to Charge Capture to pick a patient.
  useEffect(() => {
    if (!admissionId) history.replace(CHARGE_CAPTURE_PATH);
  }, [admissionId, history]);

  // The rate card discounts from the rack rate (origRate — the service master fee),
  // never from a cashier's override, so the preview depends only on service_code,
  // origRate and date. None of those change once a line is added, so this signature
  // is stable across qty/rate edits and the effect below doesn't re-fire on them.
  const previewSig = useMemo(
    () => JSON.stringify(
      lineItems
        .filter((li) => li.code || li.serviceId)  // service_code OR id — id covers uncoded services
        .map((li) => [li.serviceId, li.code, String(li.origRate ?? 0)])
    ),
    [lineItems]
  );
  // serviceId → signature already previewed, so adding a line doesn't re-request
  // the ones already resolved.
  const previewedRef = useRef({});

  // Preview corporate pricing for each priced line (debounced). Backend reads the
  // admission's billing_payer_id, so no payer/prices are sent. Non-corporate
  // admissions resolve to { applied:false } and the corporate UI stays hidden.
  useEffect(() => {
    if (skipCorporate) return;
    // Nothing to ask with until the admission date is resolved, and an admission whose date
    // cannot be found gets no corporate preview at all - a blank is honest, a guessed price
    // is not, because the save would quote something different.
    if (!admittedOn) return;
    const lines = JSON.parse(previewSig)
      .map(([serviceId, code, rackRate]) => ({ serviceId, code, rackRate, sig: `${code}|${rackRate}|${admittedOn}` }))
      .filter((li) => previewedRef.current[li.serviceId] !== li.sig);
    if (lines.length === 0) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      for (const li of lines) {
        try {
          const p = await previewCorporatePricing(admissionId, {
            service_code: li.code || undefined,
            medical_service_id: li.serviceId,  // lets the backend resolve group/classification for uncoded services
            quantity: 1,          // per-unit preview; the line's real qty never affects it
            standard_unit_price: li.rackRate,
            // The admission date, matching what capture resolves on - NOT the charge date.
            service_date: admittedOn,
          });
          if (cancelled) return;
          previewedRef.current[li.serviceId] = li.sig;
          const applied = p?.pricing_applied === true;
          setPreviews((prev) => ({
            ...prev,
            [li.serviceId]: applied
              ? {
                  applied: true,
                  corp: Number(p.corporate_unit_price),
                  std: Number(p.standard_unit_price ?? li.rackRate),
                  disc: Number(p.corporate_discount_amount ?? 0),
                }
              : { applied: false },
          }));
        } catch {
          if (cancelled) return;
          delete previewedRef.current[li.serviceId];
          setPreviews((prev) => ({ ...prev, [li.serviceId]: { applied: false } }));
        }
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [previewSig, admittedOn, skipCorporate, admissionId]);

  // Effective unit price for a line: corporate when a contract applies and not skipped.
  const linePricing = (li) => {
    const pv = previews[li.serviceId];
    const useCorp = !skipCorporate && pv?.applied;
    return { pv, useCorp, unit: useCorp ? pv.corp : Number(li.rate || 0) };
  };
  const hasCorporateContract = Object.values(previews).some((p) => p && p.applied);
  const corpCaption = { marginTop: "8px", fontSize: "11px", fontWeight: "600", color: C.textSub };

  const handleToggleService = (svc) => {
    setLineItems(prev => {
      if (prev.some(li => li.serviceId === svc.id)) {
        return prev.filter(li => li.serviceId !== svc.id);
      }
      const apiDescription = svc.description || svc.name;
      return [...prev, {
        serviceId:      svc.id,
        code:           svc.code,
        medicalService: svc.name,
        description:    apiDescription,        // from API; editable on the form
        origDescription: apiDescription,       // baseline to detect user edits
        dept:           depts.includes(svc.groupName) ? svc.groupName : "ICU",
        qty:            1,
        // Rack fee (main_medicalservice.fee). For a corporate admission the search
        // endpoint defaults `rate` to the contracted price, so bind `fee` here —
        // the corporate price comes from the pricing preview instead.
        rate:           svc.fee || svc.rate || 0,
        origRate:       svc.fee || svc.rate || 0,   // baseline to detect a cashier override
        // Service Master flags — control whether the cashier may override the
        // rack rate / order qty > 1 for this tariff row (see ServiceMasterAdmin).
        chargeModifiable:   svc.chargeModifiable === true,
        quantityModifiable: svc.quantityModifiable !== false,
      }];
    });
  };

  const updateLineItem = (serviceId, field, value) => {
    setLineItems(prev => prev.map(li => li.serviceId === serviceId ? { ...li, [field]: value } : li));
  };

  const removeLineItem = (serviceId) => {
    setLineItems(prev => prev.filter(li => li.serviceId !== serviceId));
  };

  const grandTotal = lineItems.reduce((s, li) => s + Number(li.qty || 0) * linePricing(li).unit, 0);
  const canSubmit  = !posting && lineItems.length > 0 && lineItems.every(li => li.description);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setPosting(true);
    setError("");
    try {
      // Add Charge → bulk-post. Fee/name are resolved server-side from
      // medical_service_id; only send a remark when the user edited the description.
      const charges = lineItems.map(li => {
        const charge = {
          medical_service_id: li.serviceId,
          quantity:           Number(li.qty) || 1,
          charge_date:        date,
        };
        // Bill at rack rate — backend skips corporate rate card for this charge.
        if (skipCorporate) charge.skip_corporate_pricing = true;
        if (li.description && li.description !== li.origDescription) {
          charge.remarks = li.description;
        }
        // Only a rate-modifiable line can carry a cashier override, and only
        // when it actually differs from the resolved rack fee — every other
        // line keeps today's payload shape (fee resolved server-side).
        if (li.chargeModifiable && Number(li.rate) !== Number(li.origRate)) {
          charge.amount = String(li.rate);
        }
        return charge;
      });
      await bulkPostCharges(admissionId, charges);
      // Posted — discard the draft so the next visit starts clean.
      if (draftKey) sessionStorage.removeItem(draftKey);
      // Charge Capture reloads its charges/totals on mount, so returning to it
      // reflects the new lines without any extra signal.
      history.push(CHARGE_CAPTURE_PATH);
    } catch (err) {
      setError(err.message || "Failed to add charge");
      setPosting(false);
    }
  };

  const inputStyle = { width: "100%", padding: "9px 12px", border: `1px solid ${C.border}`, borderRadius: "7px", fontSize: "13px", color: C.text, outline: "none", boxSizing: "border-box" };
  const labelStyle = { fontSize: "11px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: "5px" };

  return (
    <IPDBillingLayout activeNav="charge">
      <div style={{ padding: "28px", display: "flex", flexDirection: "column", gap: "20px" }}>

        {/* Page header + back link */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px", flexWrap: "wrap" }}>
          <div>
            <button
              onClick={goBack}
              style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "transparent", border: "none", cursor: "pointer", color: C.textSub, fontSize: "13px", fontWeight: "600", padding: 0, marginBottom: "8px" }}
            >
              <SvgIcon name="back" size={15} color={C.textSub} /> Back to Charge Capture
            </button>
            <h2 style={{ margin: "0 0 4px", fontSize: "22px", fontWeight: "800", color: C.text }}>Add Charge</h2>
            <p style={{ margin: 0, fontSize: "13px", color: C.textSub }}>
              Encounter {admissionId}{patientName ? ` · ${patientName}` : ""}
            </p>
          </div>
        </div>

        {/* Form card */}
        <div style={{ background: "#fff", borderRadius: "12px", border: `1px solid ${C.border}`, boxShadow: "0 2px 8px rgba(13,35,63,0.07)", padding: "24px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <div>
              <label style={labelStyle}>Medical Service *</label>
              <MedicalServiceSelect
                selectedIds={lineItems.map(li => li.serviceId)}
                onToggle={handleToggleService}
                placeholder="Search and select medical service"
                admissionId={admissionId}
              />
            </div>

            {lineItems.length === 0 ? (
              <div style={{ padding: "20px", textAlign: "center", fontSize: "12px", color: C.textSub, border: `1px dashed ${C.border}`, borderRadius: "8px" }}>
                Select one or more medical services to add line items.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {lineItems.map(li => (
                  <div key={li.serviceId} style={{ border: `1px solid ${C.border}`, borderRadius: "9px", padding: "12px", background: "#fafbfc" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px", gap: "8px" }}>
                      <span style={{ fontSize: "13px", fontWeight: "700", color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{li.medicalService}</span>
                      <button onClick={() => removeLineItem(li.serviceId)}
                        style={{ background: "transparent", border: "none", cursor: "pointer", color: "#ccc", padding: "2px", display: "inline-flex", flexShrink: 0 }}
                        onMouseEnter={e => e.currentTarget.style.color = C.danger}
                        onMouseLeave={e => e.currentTarget.style.color = "#ccc"}
                      >
                        <SvgIcon name="trash" size={15} color="currentColor" />
                      </button>
                    </div>

                    <div style={{ marginBottom: "10px" }}>
                      <label style={labelStyle}>Description *</label>
                      <input value={li.description} onChange={e => updateLineItem(li.serviceId, "description", e.target.value)} placeholder="Charge description" style={inputStyle} />
                    </div>

                    <div style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
                      <div style={{ flex: 2 }}>
                        <label style={labelStyle}>Department</label>
                        <select value={li.dept} onChange={e => updateLineItem(li.serviceId, "dept", e.target.value)} style={{ ...inputStyle, background: "#fff" }}>
                          {depts.map(d => <option key={d}>{d}</option>)}
                        </select>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={labelStyle}>Qty</label>
                        {li.quantityModifiable ? (
                          <input type="number" min="1" value={li.qty} onChange={e => updateLineItem(li.serviceId, "qty", e.target.value)} style={inputStyle} />
                        ) : (
                          <input type="number" value={1} readOnly tabIndex={-1}
                            style={{ ...inputStyle, background: "#f1f5f9", color: C.textSub, cursor: "not-allowed" }} />
                        )}
                      </div>
                      <div style={{ flex: 1.3 }}>
                        <label style={labelStyle}>{linePricing(li).useCorp ? "Rack rate (₹)" : "Rate (₹)"}</label>
                        {li.chargeModifiable ? (
                          <input type="number" value={li.rate} onChange={e => updateLineItem(li.serviceId, "rate", e.target.value)} placeholder="0" style={inputStyle} />
                        ) : (
                          <input type="number" value={li.rate} readOnly tabIndex={-1} placeholder="0"
                            style={{ ...inputStyle, background: "#f1f5f9", color: C.textSub, cursor: "not-allowed" }} />
                        )}
                      </div>
                      {linePricing(li).useCorp && (
                        <div style={{ flex: 1.3 }}>
                          <label style={labelStyle}>Corporate rate (₹)</label>
                          <div style={{ padding: "9px 12px", background: C.accent, border: "1px solid #b8d4ee", borderRadius: "7px", fontSize: "13px", fontWeight: "700", color: C.text, textAlign: "right", whiteSpace: "nowrap" }}>
                            ₹{linePricing(li).unit.toLocaleString("en-IN")}
                          </div>
                        </div>
                      )}
                    </div>

                    {(!li.quantityModifiable || !li.chargeModifiable) && (
                      <div style={{ display: "flex", gap: "12px", marginTop: "6px" }}>
                        {!li.quantityModifiable && (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "11px", fontWeight: "600", color: C.textSub }}>
                            <SvgIcon name="lock" size={11} color={C.textSub} /> Qty locked by service master
                          </span>
                        )}
                        {!li.chargeModifiable && (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "11px", fontWeight: "600", color: C.textSub }}>
                            <SvgIcon name="lock" size={11} color={C.textSub} /> Rate fixed by service master
                          </span>
                        )}
                      </div>
                    )}

                    {(() => {
                      if (!li.code || !hasCorporateContract) return null;
                      if (skipCorporate) return <div style={corpCaption}>Standard (rack) price — corporate contract skipped</div>;
                      const { pv, useCorp } = linePricing(li);
                      if (useCorp) return (
                        <div style={{ ...corpCaption, color: C.success }}>
                          Contract: rack ₹{pv.std.toLocaleString("en-IN")} → corporate ₹{pv.corp.toLocaleString("en-IN")} · save ₹{pv.disc.toLocaleString("en-IN")}/unit
                        </div>
                      );
                      if (pv && !pv.applied) return <div style={corpCaption}>No corporate contract for this service — rack price</div>;
                      return <div style={corpCaption}>Checking corporate contract…</div>;
                    })()}
                  </div>
                ))}

                {hasCorporateContract && (
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", fontWeight: "600", color: C.text, padding: "2px 12px", cursor: "pointer" }}>
                    <input type="checkbox" checked={skipCorporate} onChange={e => setSkipCorporate(e.target.checked)} />
                    Use standard (rack) price — skip corporate contract
                  </label>
                )}

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", borderTop: `1px solid ${C.border}` }}>
                  <span style={{ fontSize: "12px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>Total{hasCorporateContract && !skipCorporate ? " (corporate)" : ""}</span>
                  <span style={{ fontSize: "16px", fontWeight: "800", color: C.text }}>₹{grandTotal.toLocaleString("en-IN")}</span>
                </div>
              </div>
            )}

            <div>
              <label style={labelStyle}>Date *</label>
              <IpdDateField value={date} onChange={setDate} fullWidth />
            </div>
          </div>

          {error && (
            <div style={{ marginTop: "14px", background: "#fdecea", border: "1px solid #f5c2c7", borderRadius: "8px", padding: "10px 14px", fontSize: "12px", color: "#842029", fontWeight: "600" }}>
              {error}
            </div>
          )}

          <div style={{ display: "flex", gap: "10px", marginTop: "22px" }}>
            <button onClick={goBack} disabled={posting} style={{ flex: 1, padding: "10px", background: "#fff", color: C.textSub, border: `1px solid ${C.border}`, borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: posting ? "not-allowed" : "pointer" }}>
              Cancel
            </button>
            <button onClick={handleSubmit} disabled={!canSubmit}
              style={{ flex: 2, padding: "10px", background: C.primary, color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "700", cursor: canSubmit ? "pointer" : "not-allowed", opacity: canSubmit ? 1 : 0.6 }}>
              {posting ? "Adding…" : `Add ${lineItems.length > 1 ? `${lineItems.length} Charges` : "Charge"}`}
            </button>
          </div>
        </div>
      </div>
    </IPDBillingLayout>
  );
}
